import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientImageProcessor } from '../../services/client/imageProcessor';
import ManualSelectionEditor, { ManualSelectionEditorRef } from './ManualSelectionEditor';
import { ProcessingStatus as ProcessingStatusType } from '../../types/client';
import { Country } from '../../types';
import SEO from '../SEO/SEO';
import { useProcessingSEO, useSEO } from '../../hooks/useSEO';
import { useWizard } from '../wizard/WizardContext';
import Icon from '../common/Icon';
import { ICONS } from '../../utils/fontAwesome';

interface ClientPhotoEditorProps {
  file: File;
  country: Country;
  className?: string;
}

const ClientPhotoEditor: React.FC<ClientPhotoEditorProps> = ({
  file,
  country,
  className = ''
}) => {
  const { t } = useTranslation();
  const { state, setProcessedResult, setFinalPhoto, completeStep, goToStep } = useWizard();
  const [processor] = useState(() => new ClientImageProcessor({ demoMode: false })); // Use real AI background removal for best quality
  const [status, setStatus] = useState<ProcessingStatusType>(processor.getStatus());
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showManualSelection, setShowManualSelection] = useState(false);
  const [manualProcessing, setManualProcessing] = useState(false);
  const manualEditorRef = useRef<ManualSelectionEditorRef>(null);

  // Dynamic SEO based on processing state
  const processingSEO = useProcessingSEO(country);
  const editingSEO = useSEO({ country, state: 'selecting' });
  
  // Choose SEO based on current state
  const currentSEO = showManualSelection ? editingSEO : processingSEO;

  useEffect(() => {
    const unsubscribe = processor.onStatusUpdate((newStatus) => {
      setStatus(newStatus);
    });

    const initialize = async () => {
      try {
        await processor.initialize();
        
        // Create preview URL
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
        
        // Check if we already have processed results from wizard state
        if (state.processedResult) {
          // Use existing processed result - no need to re-process
          setShowManualSelection(true);
        } else {
          // First time loading - skip background removal, just show original image
          // Background removal will be triggered manually from crop page
          const originalResult = await processor.prepareOriginalImage(file, country);
          setProcessedResult(originalResult);
          setShowManualSelection(true);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to initialize';
        console.error('Processing failed:', errorMessage);
      }
    };

    initialize();

    return () => {
      unsubscribe();
      processor.dispose();
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [file, country, processor, setProcessedResult, state.processedResult]);

  const handleManualSelectionComplete = useCallback((blob: Blob) => {
    setFinalPhoto(blob);
    setShowManualSelection(false);
    
    // Mark crop step as completed and move to download step
    completeStep('crop');
    goToStep('download');
  }, [setFinalPhoto, completeStep, goToStep]);


  return (
    <>
      <SEO
        title={currentSEO.title}
        description={currentSEO.description}
        keywords={currentSEO.keywords}
        canonical={currentSEO.canonical}
        image={currentSEO.image}
        country={currentSEO.country}
        language={currentSEO.language}
        noIndex={manualProcessing || status.progress < 100}
      />
      <div className={`${className}`}>

        {/* Light Loading Overlay - Only for editor loading */}
        {(!state.processedResult || !showManualSelection) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 mx-4 max-w-md w-full">
              <div className="text-center space-y-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Loading Photo Editor</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">Preparing your photo for editing...</p>
                </div>
                <p className="text-sm text-blue-600 dark:text-blue-400">
                  <Icon icon={ICONS.CAMERA} className="mr-2" />
                  Editor loading - this may take a moment on mobile
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Photo Selection Interface - Always rendered to maintain responsive container */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">{t('workflow.selectPhotoArea')}</h2>
              <p className="text-gray-600 dark:text-gray-300 text-center mb-6">Drag and resize the rectangle to select the area for your {country.name} passport photo</p>              
              {/* No placeholder - instant loading */}
              
              {/* Show actual interface when ready */}
              {state.processedResult && showManualSelection && (
                <ManualSelectionEditor
                  ref={manualEditorRef}
                  result={state.processedResult}
                  country={country}
                  processor={processor}
                  onFinalResult={handleManualSelectionComplete}
                  onProcessingChange={setManualProcessing}
                />
              )}
            </div>
          </div>
        </div>

      </div>
    </>
  );
};

export default ClientPhotoEditor;
